/**
 * Character-by-character scroll-reveal for rich text (strings + inline elements).
 * Each non-space character gets a <span> whose CSS class toggles between
 * hidden → active → revealed as the user scrolls. Supports standalone usage
 * or coordinated reveal inside a ScrollRevealGroup parent.
 */
import { useRef, useMemo, useEffect, useId, useCallback, ReactNode, Children, isValidElement, cloneElement } from "react";
import { useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { useScrollRevealGroup } from "./ScrollRevealGroup";

interface ScrollRevealRichTextProps {
  children: ReactNode;
  className?: string;
}

type Segment = 
  | { type: 'text'; content: string }
  | { type: 'element'; element: React.ReactElement; textContent: string };

function extractTextContent(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) return node.map(extractTextContent).join('');
  if (isValidElement(node)) return extractTextContent(node.props.children);
  return '';
}

function flattenChildren(children: ReactNode): Segment[] {
  const segments: Segment[] = [];
  Children.forEach(children, (child) => {
    if (typeof child === 'string') {
      segments.push({ type: 'text', content: child });
    } else if (typeof child === 'number') {
      segments.push({ type: 'text', content: String(child) });
    } else if (isValidElement(child)) {
      const textContent = extractTextContent(child);
      segments.push({ type: 'element', element: child, textContent });
    }
  });
  return segments;
}

function countChars(segments: Segment[]): number {
  return segments.reduce((acc, seg) => {
    const text = seg.type === 'text' ? seg.content : seg.textContent;
    return acc + text.length;
  }, 0);
}

function updateCharClasses(spans: (HTMLSpanElement | null)[], progress: number, totalChars: number) {
  const cursor = progress * Math.max(totalChars - 1, 1);
  for (let i = 0; i < spans.length; i++) {
    const span = spans[i];
    if (!span) continue;
    const local = cursor - i;

    if (local >= 1) {
      if (!span.classList.contains("sr-char-revealed")) {
        span.classList.remove("sr-char-active");
        span.classList.add("sr-char-revealed");
      }
    } else if (local > 0) {
      if (!span.classList.contains("sr-char-active")) {
        span.classList.remove("sr-char-revealed");
        span.classList.add("sr-char-active");
      }
    } else {
      if (span.classList.contains("sr-char-active") || span.classList.contains("sr-char-revealed")) {
        span.classList.remove("sr-char-active", "sr-char-revealed");
      }
    }
  }
}

const ScrollRevealRichText = ({ children, className = "" }: ScrollRevealRichTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const charSpansRef = useRef<(HTMLSpanElement | null)[]>([]);
  const instanceId = useId();
  const group = useScrollRevealGroup();

  const segments = useMemo(() => flattenChildren(children), [children]);
  const totalChars = useMemo(() => countChars(segments), [segments]);

  useEffect(() => {
    charSpansRef.current = new Array(totalChars).fill(null);
  }, [totalChars]);

  useEffect(() => {
    if (group && containerRef.current) {
      group.register(instanceId, containerRef.current);
      return () => group.unregister(instanceId);
    }
  }, [group, instanceId]);

  const standaloneScroll = useScroll({
    target: containerRef,
    offset: ["start 0.95", "end 0.05"],
  });

  const isGrouped = !!group;
  const groupProgress = group?.scrollYProgress;
  const range = group?.getRange(instanceId);

  const mappedProgress = useTransform(
    groupProgress ?? standaloneScroll.scrollYProgress,
    (v: number) => {
      if (!isGrouped || !range) return v;
      const { start, end } = range;
      if (end <= start) return 0;
      return Math.min(1, Math.max(0, (v - start) / (end - start)));
    }
  );

  useMotionValueEvent(mappedProgress, "change", (latest) => {
    updateCharClasses(charSpansRef.current, latest, totalChars);
  });

  const makeCharRef = useCallback((globalIndex: number) => (el: HTMLSpanElement | null) => {
    charSpansRef.current[globalIndex] = el;
  }, []);

  // Render helpers
  const renderCharSpans = (text: string, keyPrefix: string, startGlobalIdx: number) => {
    const words = text.split(" ");
    let charOffset = 0;
    return words.map((word, wi) => {
      const wordStart = charOffset;
      charOffset += word.length;

      const chars = word.split("").map((char, ci) => (
        <span
          key={ci}
          ref={makeCharRef(startGlobalIdx + wordStart + ci)}
          className="sr-char inline"
        >
          {char}
        </span>
      ));

      // Wrap space in a tracked span so it gets reveal classes (fixes link underline gaps)
      let space: React.ReactNode = null;
      if (wi < words.length - 1) {
        space = (
          <span
            key={`${keyPrefix}-s${wi}`}
            ref={makeCharRef(startGlobalIdx + charOffset)}
            className="sr-char inline"
          >
            {" "}
          </span>
        );
        charOffset += 1;
      }

      return (
        <span key={`${keyPrefix}-w${wi}`} className="inline">
          {chars}
          {space}
        </span>
      );
    });
  };

  let globalCharIndex = 0;

  const renderSegment = (segment: Segment, segmentIndex: number) => {
    const startIdx = globalCharIndex;
    const text = segment.type === 'text' ? segment.content : segment.textContent;
    globalCharIndex += text.length;

    const charContent = renderCharSpans(text, String(segmentIndex), startIdx);

    if (segment.type === 'text') {
      return charContent;
    }
    return cloneElement(segment.element, {
      key: segmentIndex,
      ref: undefined,
      children: charContent,
    });
  };

  globalCharIndex = 0;
  const renderedContent = segments.map((seg, idx) => renderSegment(seg, idx));

  return (
    <div ref={containerRef} className="relative w-full">
      <p className={`${className} break-words whitespace-pre-wrap`}>
        {renderedContent}
      </p>
    </div>
  );
};

export default ScrollRevealRichText;
